import { writesApi } from './api';

// ── Withdrawals ────────────────────────────────────────────────────────────
// Request → review → pay out, for every wallet owner (customer, rider,
// business, branch). All writes live in Spring Boot under /admin/withdrawals.
//
// There is no payout integration yet, and the accounting does not depend on
// one: completing a withdrawal debits the wallet and credits 2007 Withdrawals
// Payable — a real liability saying "we owe this person cash we have not yet
// handed over". The shilling itself leaves by cheque, M-Pesa or hand, and the
// reference recorded at completion is the proof it did.

export type WithdrawalStatus =
  | 'pending'
  | 'approved'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'cancelled'
  | 'failed';

/** Statuses that still hold funds — the queue a human has to work. */
export const OPEN_WITHDRAWAL_STATUSES: WithdrawalStatus[] = [
  'pending',
  'approved',
  'processing',
];

export interface PayoutMethod {
  code: string;
  name: string;
  description?: string | null;
  /** Keys the payout details must carry — validated server-side too. */
  required_fields?: string[] | null;
  is_automated?: boolean;
}

/**
 * Rows come straight from `withdrawal_requests` (the service returns the row),
 * so field names are snake_case — matching the DB rather than renaming in two
 * places and drifting.
 */
export interface WithdrawalRequest {
  id: string;
  reference: string;
  wallet_id: string;
  owner_type: 'user' | 'business' | 'branch' | 'system' | string;
  owner_id: string | null;

  amount: number;
  fee_amount: number;
  net_amount: number;
  currency: string;

  payout_method: string;
  payout_details: Record<string, unknown> | null;
  payout_reference: string | null;

  status: WithdrawalStatus | string;

  requested_by: string;
  requested_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  /** Who released the money. The first question asked when a payout is queried. */
  completed_by: string | null;
  completed_at: string | null;

  rejection_reason: string | null;
  notes: string | null;

  hold_journal_entry_id: string | null;
  settlement_journal_entry_id: string | null;
}

export const withdrawalService = {
  /** Open queue when `status` is omitted — oldest first, because a payout
   *  waiting three days is worse than one waiting three minutes. */
  queue: (params?: { status?: string; limit?: number }) =>
    writesApi.get<WithdrawalRequest[]>(
      '/admin/withdrawals',
      params as Record<string, unknown>,
    ),

  get: (id: string) => writesApi.get<WithdrawalRequest>(`/admin/withdrawals/${id}`),

  methods: () => writesApi.get<PayoutMethod[]>('/withdrawals/methods'),

  approve: (id: string, reason?: string) =>
    writesApi.post<WithdrawalRequest>(`/admin/withdrawals/${id}/approve`, { reason }),

  reject: (id: string, reason: string) =>
    writesApi.post<WithdrawalRequest>(`/admin/withdrawals/${id}/reject`, { reason }),

  /** `payoutReference` is required by the backend — a completed payout with
   *  nothing to check it against is indistinguishable from a mistake. */
  complete: (id: string, payoutReference: string, notes?: string) =>
    writesApi.post<WithdrawalRequest>(`/admin/withdrawals/${id}/complete`, {
      payoutReference,
      notes,
    }),

  fail: (id: string, reason: string) =>
    writesApi.post<WithdrawalRequest>(`/admin/withdrawals/${id}/fail`, { reason }),
};

/** Chip styling per status. Terminal-good, terminal-bad and in-flight read
 *  differently at a glance so a queue can be scanned rather than parsed. */
export function withdrawalStatusChip(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-[var(--state-success-soft,rgba(22,163,74,0.10))] text-[var(--state-success)]';
    case 'rejected':
    case 'failed':
      return 'bg-[var(--state-danger-soft,rgba(220,38,38,0.10))] text-[var(--state-danger)]';
    case 'cancelled':
      return 'bg-[var(--bg-base)] text-[var(--text-muted)]';
    default:
      return 'bg-[var(--state-warning-soft,rgba(217,119,6,0.10))] text-[var(--state-warning,#d97706)]';
  }
}
